import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../Firebase';
import { 
  LayoutDashboard, Settings, BarChart3, Package, BookOpen, 
  Briefcase, FolderOpen, LogOut, Menu, X, ChevronRight, User, Loader2,
  Activity, ClipboardList
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function AdminLayout() {
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Secure auth gate check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        navigate('/admin/login');
      } else {
        setUser(currentUser);
      }
      setCheckingAuth(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  // Inactivity Auto-Logout (Session & Security Management)
  useEffect(() => {
    if (checkingAuth || !user) return;

    let timeoutId;
    const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes of inactivity

    const triggerAutoLogout = async () => {
      try {
        await signOut(auth);
        navigate('/admin/login', { state: { message: 'Session expired due to inactivity. Please sign in again.' } });
      } catch (err) {
        console.error('Auto-logout error:', err);
      }
    };

    const resetTimer = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(triggerAutoLogout, INACTIVITY_TIMEOUT);
    };

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];

    resetTimer();

    activityEvents.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [user, checkingAuth, navigate]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/admin/login');
    } catch (err) {
      console.error('Error logging out:', err);
    }
  };

  const menuItems = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Manage Services', path: '/admin/services', icon: Settings },
    { name: 'Manage Growth Stats', path: '/admin/stats', icon: BarChart3 },
    { name: 'Manage Products', path: '/admin/products', icon: Package },
    { name: 'Manage Projects', path: '/admin/projects', icon: Activity },
    { name: 'Manage Blogs', path: '/admin/blogs', icon: BookOpen },
    { name: 'Manage Jobs', path: '/admin/jobs', icon: Briefcase },
    { name: 'Product Enquiries', path: '/admin/enquiries', icon: ClipboardList },
    { name: 'Consultation Enquiry', path: '/admin/consultations', icon: FolderOpen }
  ];

  // Close mobile drawer on route shifts
  useEffect(() => {
    setSidebarOpen(false);
  }, [location]);

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-brand-navy flex flex-col items-center justify-center text-white space-y-4">
        <Loader2 className="animate-spin text-brand-blue" size={40} />
        <p className="text-sm font-semibold tracking-wider font-poppins text-brand-silver/60">Securing Admin Tunnel...</p>
      </div>
    );
  }

  return (
    <div className="h-screen bg-brand-light flex font-sans overflow-hidden">
      
      {/* 1. Sidebar Navigation (Desktop) */}
      <aside className="hidden lg:flex flex-col w-72 bg-brand-navy border-r border-white/5 shrink-0 h-full relative overflow-hidden">
        {/* Glowing visual indicators */}
        <div className="absolute top-0 right-0 w-48 h-48 bg-brand-blue/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-brand-green/5 rounded-full blur-2xl pointer-events-none" />

        {/* Brand Header */}
        <div className="p-6 border-b border-white/5 flex justify-center items-center relative z-10">
          <img 
            src="/assets/footer logo.png" 
            alt="Kiran Distributors" 
            className="h-20 w-auto object-contain transition-transform duration-300 hover:scale-105 cursor-pointer"
          />
        </div>

        {/* Sidebar Nav Links */}
        <nav className="flex-grow p-6 space-y-1.5 relative z-10 overflow-y-auto">
          {menuItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.path}
              className={({ isActive }) => 
                `flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm font-semibold font-poppins transition-all duration-200 group ${
                  isActive 
                    ? 'bg-brand-blue text-white shadow-lg shadow-brand-blue/20' 
                    : 'text-brand-silver/60 hover:text-white hover:bg-white/5'
                }`
              }
            >
              <item.icon size={18} className="shrink-0 transition-transform duration-200 group-hover:scale-105" />
              <span>{item.name}</span>
              <ChevronRight size={14} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* 2. Desktop Workspace & Mobile Drawer Wrapper */}
      <div className="flex-grow flex flex-col min-w-0 h-full overflow-hidden">
        
        {/* Premium Top Navbar Header (Desktop only) */}
        <header className="hidden lg:flex items-center justify-between px-10 py-5 bg-white border-b border-brand-silver/40 shrink-0 z-30 shadow-sm">
          {/* Left side: Workspace tag */}
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-brand-blue animate-pulse-slow" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400 font-poppins">
              Secured Administration Area
            </span>
          </div>

          {/* Right side: Admin profile details and Logout button */}
          <div className="flex items-center gap-6">
            
            {/* User Profile Badge */}
            <div className="flex items-center gap-3 bg-brand-light border border-brand-silver/60 py-1.5 px-3.5 rounded-2xl shadow-inner">
              <div className="w-8 h-8 rounded-full bg-brand-blue text-white flex items-center justify-center font-bold text-sm shadow-md shadow-brand-blue/20">
                <User size={15} />
              </div>
              <div className="text-left">
                <div className="text-xs font-extrabold text-brand-navy font-montserrat leading-tight">Administrator</div>
                <div className="text-[10px] text-gray-500 font-poppins font-medium leading-none mt-0.5">{user?.email}</div>
              </div>
            </div>

            {/* Logout Access Trigger */}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 py-2 px-4 rounded-xl border border-brand-silver/80 text-gray-500 hover:text-brand-blue bg-white hover:bg-brand-blue/5 hover:border-brand-blue/30 transition-all font-semibold font-poppins text-xs shadow-sm hover:shadow"
            >
              <LogOut size={13} className="stroke-[2.5]" />
              <span>Logout Access</span>
            </button>

          </div>
        </header>

        {/* Mobile Header Bar */}
        <header className="lg:hidden bg-brand-navy border-b border-white/5 px-6 py-4 flex items-center justify-between z-40 shrink-0">
          <div className="flex items-center">
            <img 
              src="/assets/footer logo.png" 
              alt="Kiran Distributors" 
              className="h-16 w-auto object-contain transition-transform duration-300 hover:scale-105 cursor-pointer"
            />
          </div>
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg bg-white/5 text-brand-silver/60 hover:text-white"
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </header>

        {/* Mobile Sidebar overlay */}
        <AnimatePresence>
          {sidebarOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.5 }}
                exit={{ opacity: 0 }}
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden fixed inset-0 bg-black z-40"
              />
              
              {/* Drawer Container */}
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className="lg:hidden fixed top-0 bottom-0 left-0 w-72 bg-brand-navy border-r border-white/5 z-50 flex flex-col"
              >
                <div className="p-6 border-b border-white/5 flex items-center justify-between">
                  <img 
                    src="/assets/footer logo.png" 
                    alt="Kiran Distributors" 
                    className="h-16 w-auto object-contain transition-transform duration-300 hover:scale-105 cursor-pointer"
                  />
                  <button 
                    onClick={() => setSidebarOpen(false)}
                    className="p-2 rounded-lg text-brand-silver/60 hover:text-white"
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Administrator Profile (Moved below logo) */}
                <div className="px-6 py-4 border-b border-white/5">
                  <div className="flex items-center gap-3 bg-white/5 p-3 rounded-xl">
                    <div className="w-8 h-8 rounded-full bg-brand-blue/20 text-brand-blue flex items-center justify-center font-bold">
                      <User size={14} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-white font-montserrat truncate">Administrator</p>
                      <p className="text-[10px] text-brand-silver/45 font-poppins truncate">{user?.email}</p>
                    </div>
                  </div>
                </div>

                <nav className="flex-grow p-6 space-y-1.5 overflow-y-auto">
                  {menuItems.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.path}
                      className={({ isActive }) => 
                        `flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm font-semibold font-poppins transition-all duration-200 ${
                          isActive 
                            ? 'bg-brand-blue text-white shadow-lg' 
                            : 'text-brand-silver/60 hover:text-white hover:bg-white/5'
                        }`
                      }
                    >
                      <item.icon size={18} />
                      <span>{item.name}</span>
                    </NavLink>
                  ))}
                  {/* Logout Access in Mobile Menu */}
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm font-semibold font-poppins text-brand-silver/60 hover:text-brand-blue hover:bg-brand-blue/5 transition-all text-left"
                  >
                    <LogOut size={18} className="shrink-0" />
                    <span>Logout Access</span>
                  </button>
                </nav>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* 3. Main Outlet Panel */}
        <main className="flex-grow overflow-y-auto p-6 sm:p-10 relative">
          <Outlet />
        </main>
      </div>

    </div>
  );
}
